import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import BreadcrumbNav from '../components/BreadcrumbNav';
import {
  Container,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { formatDate } from '../utils/dateUtils';
import { windowsTableStyles } from '../styles/windowsTableStyles';
import { formatExecutionDetails } from '../utils/workflowExecutionDetails';

const tableStyles = windowsTableStyles();

const WorkflowExecutionHistoryPage = () => {
  const { workflowId } = useParams();
  const { t } = useTranslation(['workflows', 'common', 'buttons', 'navigation']);
  const [workflowName, setWorkflowName] = useState('');
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsExecution, setDetailsExecution] = useState(null);

  useEffect(() => {
    if (workflowId) {
      fetchWorkflow();
    }
  }, [workflowId]);

  useEffect(() => {
    if (workflowId) {
      fetchExecutions();
    }
  }, [workflowId, page, rowsPerPage]);

  const fetchWorkflow = async () => {
    try {
      const { data } = await axios.get(`/api/workflows/${workflowId}`);
      setWorkflowName(data.name || '');
    } catch (e) {
      console.error('Error fetching workflow:', e);
    }
  };

  const fetchExecutions = async () => {
    if (!workflowId) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
      });
      const { data } = await axios.get(`/api/workflows/${workflowId}/executions?${params.toString()}`);
      setExecutions(data.executions || []);
      setTotal(data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching executions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'running': return 'warning';
      case 'pending': return 'default';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      completed: t('workflows:status.completed') || 'Completed',
      failed: t('workflows:status.failed') || 'Failed',
      running: t('workflows:status.running') || 'Running',
      pending: t('workflows:status.pending') || 'Pending',
      cancelled: t('workflows:status.cancelled') || 'Cancelled',
    };
    return labels[status] || status;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    return formatDate(d) + ' ' + d.toLocaleTimeString();
  };

  const getActionsSummary = (executionData) => {
    if (!executionData || typeof executionData !== 'object') return '0 actions';
    const steps = Object.keys(executionData).filter((k) => k.startsWith('step_'));
    const successful = steps.filter((k) => executionData[k]?.success).length;
    const failed = steps.filter((k) => executionData[k]?.success === false).length;
    return `${steps.length} actions (${successful} successful, ${failed} failed)`;
  };

  const filteredExecutions = statusFilter === 'all'
    ? executions
    : executions.filter((e) => e.status === statusFilter);

  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: 'white',
        boxShadow: '0px 4px 16px rgba(0,0,0,0.013)',
        pt: 2,
        pb:4,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <BreadcrumbNav
          title={workflowName ? `${t('workflows:execution_history') || 'Execution History'}: ${workflowName}` : (t('workflows:execution_history') || 'Execution History')}
          breadcrumbs={[
            { label: t('navigation:home'), href: '/' },
            { label: t('navigation:workflows'), href: '/workflows' },
          ]}
          showBreadcrumbs={true}
        />
      </Box>

      <Box sx={{ border: '1px solid #d1d1d1', p: 2, mb: 3, pb: 3 }}>
        {loading && executions.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : executions.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="text.secondary">
              {t('workflows:no_executions') || 'No executions found for this workflow.'}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('workflows:filter_status') || 'Filter by Status'}</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label={t('workflows:filter_status') || 'Filter by Status'}
                >
                  <MenuItem value="all">{t('common:all') || 'All'}</MenuItem>
                  <MenuItem value="completed">{t('workflows:status.completed') || 'Completed'}</MenuItem>
                  <MenuItem value="failed">{t('workflows:status.failed') || 'Failed'}</MenuItem>
                  <MenuItem value="running">{t('workflows:status.running') || 'Running'}</MenuItem>
                  <MenuItem value="pending">{t('workflows:status.pending') || 'Pending'}</MenuItem>
                  <MenuItem value="cancelled">{t('workflows:status.cancelled') || 'Cancelled'}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer sx={{ maxHeight: 500, overflowY: 'auto', width: '100%', marginBottom:'1rem'}}>
              <Table size="small" sx={tableStyles.container}>
                <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 1000 }}>
                  <TableRow sx={{ backgroundColor: '#ffffff' }}>
                    <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('workflows:date_time') || 'Date/Time'}</TableCell>
                    <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('workflows:status_label') || 'Status'}</TableCell>
                    <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('workflows:triggered_by') || 'Triggered By'}</TableCell>
                    <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('workflows:actions_summary') || 'Actions Summary'}</TableCell>
                    <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('workflows:error') || 'Error'}</TableCell>
                    <TableCell sx={{ ...tableStyles.headerCell, fontWeight: 600, padding: '4px 16px', fontSize: '12px', borderBottom: '1px solid #d0d0d0' }}>{t('common:details') || 'Details'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredExecutions.map((execution) => (
                    <TableRow key={execution._id} sx={{ ...tableStyles.row, borderBottom: '1px solid #d0d0d0' }}>
                      <TableCell sx={{ ...tableStyles.cell, padding: '2px 16px', fontSize: '12px', verticalAlign: 'middle' }}>{formatDateTime(execution.startedAt)}</TableCell>
                      <TableCell sx={{ ...tableStyles.cell, padding: '2px 16px', fontSize: '12px', verticalAlign: 'middle' }}>
                        <Chip label={getStatusLabel(execution.status)} color={getStatusColor(execution.status)} size="small" />
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell, padding: '2px 16px', fontSize: '12px', verticalAlign: 'middle' }}>
                        {execution.triggeredBy
                          ? `${execution.triggeredBy.firstName || ''} ${execution.triggeredBy.lastName || ''}`.trim() || execution.triggeredBy.email
                          : '-'}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell, padding: '2px 16px', fontSize: '12px', verticalAlign: 'middle' }}>
                        {getActionsSummary(execution.executionData)}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell, padding: '2px 16px', fontSize: '12px', verticalAlign: 'middle' }}>
                        {execution.error ? (
                          <Typography variant="body2" color="error" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {execution.error}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell, padding: '2px 16px', fontSize: '12px', verticalAlign: 'middle' }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setDetailsExecution(execution);
                            setDetailsModalOpen(true);
                          }}
                        >
                          {t('common:show') || 'Show'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Box>

      <Dialog open={detailsModalOpen} onClose={() => { setDetailsModalOpen(false); setDetailsExecution(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600, fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }}>
          {t('workflows:execution_details') || 'Execution Details'}
        </DialogTitle>
        <DialogContent>
          {detailsExecution && (() => {
            const { eventContext, steps } = formatExecutionDetails(detailsExecution);
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                {detailsExecution.error && (
                  <Box sx={{ p: 1.5, backgroundColor: '#ffebee', borderRadius: 1 }}>
                    <Typography variant="body2" color="error" sx={{ fontSize: '12px' }}>
                      <strong>{t('workflows:error') || 'Error'}:</strong> {detailsExecution.error}
                    </Typography>
                  </Box>
                )}
                {detailsExecution.status === 'cancelled' && (
                  <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                      <strong>{t('workflows:status.cancelled') || 'Cancelled'}</strong>
                      {detailsExecution.userResponse === 'no' && ` - ${t('workflows:user_cancelled') || 'User cancelled'}`}
                    </Typography>
                  </Box>
                )}
                {detailsExecution.confirmedAt && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '11px', display: 'block', mb: 0.5 }}>
                      {t('workflows:confirmed_at') || 'Confirmed at'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}>{formatDateTime(detailsExecution.confirmedAt)}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '11px', display: 'block', mb: 0.5 }}>
                    {t('workflows:event_context') || 'Event context'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px' }}>{eventContext}</Typography>
                </Box>
                {steps.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '11px', display: 'block', mb: 1 }}>
                      {t('workflows:step_results') || 'Step results'}
                    </Typography>
                    {steps.map((s) => (
                      <Box key={s.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        {s.success ? (
                          <CheckCircleIcon sx={{ fontSize: 18, color: '#2e7d32', mt: 0.2 }} />
                        ) : (
                          <ErrorIcon sx={{ fontSize: 18, color: '#d32f2f', mt: 0.2 }} />
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>{s.actionLabel}</Typography>
                          <Typography variant="body2" sx={{ fontSize: '12px', color: '#555' }}>{s.summary}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDetailsModalOpen(false); setDetailsExecution(null); }} variant="outlined" sx={{ borderRadius: 0, textTransform: 'none' }}>
            {t('buttons:close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WorkflowExecutionHistoryPage;
